/**
 * ═══════════════════════════════════════════════════════════════
 * WASP Frontend v2.0 - Metrics Overview Component
 * ═══════════════════════════════════════════════════════════════
 * 
 * @file components/MetricsOverview.tsx
 * @version 1.0.0
 * @description Dashboard principal con cards de métricas destacadas
 * @created 2025-10-30 - Fase 2 Session 1
 * 
 * FEATURES:
 * - 4 cards de métricas principales
 * - Números grandes con animación
 * - Indicadores de tendencia (↑↓)
 * - Comparativas con periodo anterior
 * - Colores según estado (verde/rojo)
 * - Icons de FontAwesome temáticos
 * - Loading skeleton states
 * - Responsive grid (2x2 en desktop, 1 columna en mobile)
 * - Tema azul consistente
 */

import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaperPlane,
  faInbox,
  faChartLine,
  faClock,
  faArrowUp,
  faArrowDown,
  faMinus
} from '@fortawesome/free-solid-svg-icons';

// Types
import {
  MetricsOverviewProps,
  PeriodComparison
} from '../types/performance_types';

// ═══════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════

interface MetricCardProps {
  title: string;
  value: number | string;
  icon: any;
  iconColor: string;
  comparison?: PeriodComparison;
  suffix?: string;
  decimals?: number;
  loading?: boolean;
}

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

/**
 * Formatea un número con separadores de miles
 */
const formatNumber = (num: number, decimals: number = 0): string => {
  if (isNaN(num)) return '0';
  
  if (decimals > 0) {
    return num.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Formatea tiempo en minutos a formato legible
 */
const formatTime = (minutes: number): string => {
  if (minutes < 1) {
    return '< 1 min';
  }
  
  if (minutes < 60) {
    return `${Math.round(minutes)} min`;
  }
  
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  
  if (mins === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${mins}m`;
};

/**
 * Obtiene el color según la tendencia
 */
const getTrendColor = (trend: 'up' | 'down' | 'neutral'): string => {
  switch (trend) {
    case 'up':
      return '#198754'; // Bootstrap success green
    case 'down':
      return '#dc3545'; // Bootstrap danger red
    case 'neutral':
      return '#6c757d'; // Bootstrap secondary gray
    default:
      return '#6c757d';
  }
};

/**
 * Obtiene el icono según la tendencia
 */
const getTrendIcon = (trend: 'up' | 'down' | 'neutral') => {
  switch (trend) {
    case 'up':
      return faArrowUp;
    case 'down':
      return faArrowDown;
    case 'neutral':
      return faMinus;
    default:
      return faMinus;
  }
};

// ═══════════════════════════════════════════════════════════════
// METRIC CARD COMPONENT
// ═══════════════════════════════════════════════════════════════

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  iconColor,
  comparison,
  suffix = '',
  decimals = 0,
  loading = false
}) => {
  
  const [displayValue, setDisplayValue] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  // Animación del número
  useEffect(() => {
    if (loading || typeof value !== 'number') return;

    setIsAnimating(true);
    const targetValue = value;
    const duration = 1000; // 1 segundo
    const steps = 60;
    const increment = targetValue / steps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      if (currentStep >= steps) {
        setDisplayValue(targetValue);
        setIsAnimating(false);
        clearInterval(timer);
      } else {
        setDisplayValue(increment * currentStep);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value, loading]);

  return (
    <Card style={{
      border: 'none',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: 'var(--bs-body-bg)',
      height: '100%',
      transition: 'transform 0.2s, box-shadow 0.2s',
      cursor: 'default'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
    }}>
      <Card.Body className="d-flex flex-column justify-content-between p-4">
        
        {/* Header con icono */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: `${iconColor}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            <FontAwesomeIcon 
              icon={icon} 
              size="lg"
              style={{ color: iconColor }}
            />
          </div>

          {/* Badge de comparación */}
          {comparison && !loading && (
            <Badge 
              bg="light"
              text="dark"
              style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                padding: '0.375rem 0.625rem',
                border: `1px solid ${getTrendColor(comparison.trend)}20`,
                color: getTrendColor(comparison.trend)
              }}>
              <FontAwesomeIcon 
                icon={getTrendIcon(comparison.trend)} 
                size="xs"
                className="me-1"
              />
              {Math.abs(comparison.changePercent).toFixed(1)}%
            </Badge>
          )}
        </div>

        {/* Valor principal */}
        <div className="mb-2">
          {loading ? (
            <div className="d-flex align-items-center">
              <Spinner animation="border" size="sm" variant="primary" className="me-2" />
              <span className="text-muted">Cargando...</span>
            </div>
          ) : (
            <h2 
              className="mb-0"
              style={{
                fontSize: '2.5rem',
                fontWeight: '700',
                color: 'var(--bs-body-color)',
                letterSpacing: '-0.02em',
                transition: 'color 0.3s'
              }}>
              {typeof value === 'number' 
                ? formatNumber(displayValue, decimals)
                : value}
              <span style={{ 
                fontSize: '1.25rem', 
                fontWeight: '500',
                marginLeft: '0.25rem',
                color: 'var(--bs-secondary)'
              }}>
                {suffix}
              </span>
            </h2>
          )}
        </div>

        {/* Título */}
        <div>
          <p 
            className="mb-0"
            style={{
              fontSize: '0.875rem',
              fontWeight: '500',
              color: 'var(--bs-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
            {title}
          </p>

          {/* Texto de comparación */}
          {comparison && !loading && (
            <small 
              className="text-muted"
              style={{ fontSize: '0.75rem' }}>
              {comparison.change > 0 ? '+' : ''}
              {formatNumber(comparison.change)} vs periodo anterior
            </small>
          )}
        </div>

      </Card.Body>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════
// METRICS OVERVIEW COMPONENT
// ═══════════════════════════════════════════════════════════════

const MetricsOverview: React.FC<MetricsOverviewProps> = ({
  metrics,
  comparison,
  loading = false,
  compact = false
}) => {

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────

  return (
    <Row className={`g-${compact ? '2' : '3'}`}>
      
      {/* Card 1: Mensajes Enviados */}
      <Col xs={12} sm={6} lg={3}>
        <MetricCard
          title="Mensajes Enviados"
          value={metrics.totalSent}
          icon={faPaperPlane}
          iconColor="#0d6efd" // Primary blue
          comparison={comparison?.sent}
          loading={loading}
        />
      </Col>

      {/* Card 2: Mensajes Recibidos */}
      <Col xs={12} sm={6} lg={3}>
        <MetricCard
          title="Mensajes Recibidos"
          value={metrics.totalReceived}
          icon={faInbox}
          iconColor="#0dcaf0" // Info cyan
          comparison={comparison?.received}
          loading={loading}
        />
      </Col>

      {/* Card 3: Tasa de Respuesta */}
      <Col xs={12} sm={6} lg={3}>
        <MetricCard
          title="Tasa de Respuesta"
          value={metrics.responseRate}
          icon={faChartLine}
          iconColor="#198754" // Success green
          suffix="%"
          decimals={1}
          comparison={comparison?.responseRate}
          loading={loading}
        />
      </Col>

      {/* Card 4: Tiempo Promedio */}
      <Col xs={12} sm={6} lg={3}>
        <MetricCard
          title="Tiempo Promedio"
          value={formatTime(metrics.avgResponseTime)}
          icon={faClock}
          iconColor="#fd7e14" // Orange
          comparison={comparison?.avgResponseTime}
          loading={loading}
        />
      </Col>

    </Row>
  );
};

export default MetricsOverview;